import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material'
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Language as LanguageIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { UserService } from '../services/userService'
import { SUPPORTED_LANGUAGES } from '../i18n'
import { UserDetail } from '../types/user'
import ProfileEditModal from '../components/profile/ProfileEditModal'
import { MfaSetupSection } from '../components/profile/MfaSetupSection'

const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation('profile')
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)

  useEffect(() => {
    if (user?.id) {
      fetchProfile()
    }
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const response = await UserService.getUser(user!.id)
      setProfile(response)
      setError(null)
    } catch (err: any) {
      console.error('プロフィール取得エラー:', err)
      setError(err.response?.data?.error || t('loadError'))
    } finally {
      setLoading(false)
    }
  }

  const getRoleDisplayName = (role: string): string => {
    return t(`roles.${role}`, { defaultValue: role })
  }

  const getRoleColor = (role: string) => {
    const colors: Record<string, 'error' | 'warning' | 'default'> = {
      SUPER_ADMIN: 'error',
      CORPORATE_ADMIN: 'warning',
      USER: 'default',
    }
    return colors[role] || 'default'
  }

  const getLanguageDisplayName = (): string => {
    const lang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
    return lang ? `${lang.nativeLabel} (${lang.label})` : i18n.language
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchProfile}>
          {t('retry')}
        </Button>
      </Container>
    )
  }

  if (!profile) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">{t('notFound')}</Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          color="primary"
          onClick={() => setEditModalOpen(true)}
        >
          {t('edit')}
        </Button>
      </Box>

      <Stack spacing={3}>
        {/* 基本情報 - 全幅 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            {t('basicInfo.title')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <PersonIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('basicInfo.name')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {profile.name}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <EmailIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('basicInfo.email')}
                </Typography>
                <Typography variant="body1">{profile.email}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <BusinessIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('basicInfo.department')}
                </Typography>
                <Typography variant="body1">
                  {profile.department || t('basicInfo.notSet')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <WorkIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('basicInfo.position')}
                </Typography>
                <Typography variant="body1">{profile.position || t('basicInfo.notSet')}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <SecurityIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('basicInfo.role')}
                </Typography>
                <Chip
                  label={getRoleDisplayName(profile.role)}
                  color={getRoleColor(profile.role)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <LanguageIcon color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('basicInfo.language')}
                </Typography>
                <Typography variant="body1">{getLanguageDisplayName()}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* アカウント情報 & 2段階認証 - 横並び */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* アカウント情報 */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {t('accountInfo.title')}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('accountInfo.status')}
                  </Typography>
                  <Chip
                    label={profile.isActive ? t('accountInfo.active') : t('accountInfo.inactive')}
                    color={profile.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('accountInfo.createdAt')}
                  </Typography>
                  <Typography variant="body1">{formatDate(profile.createdAt)}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('accountInfo.updatedAt')}
                  </Typography>
                  <Typography variant="body1">{formatDate(profile.updatedAt)}</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* 2段階認証設定 */}
          <Box sx={{ flex: 1 }}>
            <MfaSetupSection />
          </Box>
        </Box>
      </Stack>

      {/* プロフィール編集モーダル */}
      {profile && (
        <ProfileEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={fetchProfile}
          userId={profile.id}
          currentProfile={{
            name: profile.name,
            email: profile.email,
            department: profile.department,
            position: profile.position,
            role: profile.role,
          }}
        />
      )}
    </Container>
  )
}

export default ProfilePage
